import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiLink } from "react-icons/fi";

// Native editing commands retain the browser's caret and undo history.
export default function SelectionToolbar({ editorRef, onChange, disabled }) {
  const toolbarRef = useRef(null);
  const savedRange = useRef(null);
  const [position, setPosition] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const update = () => {
      if (toolbarRef.current?.contains(document.activeElement)) return;
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      if (disabled || !range || range.collapsed || !editorRef.current?.contains(range.commonAncestorContainer)) {
        setPosition(null); setLinkOpen(false); return;
      }
      savedRange.current = range.cloneRange();
      const rect = range.getBoundingClientRect();
      setPosition({ position: "fixed", zIndex: 2147483001,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 290)),
        top: rect.top >= 52 ? rect.top - 46 : rect.bottom + 6,
        maxWidth: "calc(100vw - 16px)",
      });
    };
    document.addEventListener("selectionchange", update);
    const focusToolbar = (event) => {
      if (event.key === "Escape") { setPosition(null); setLinkOpen(false); }
      if (event.altKey && event.key === "F10" && editorRef.current?.contains(event.target)) {
        event.preventDefault(); toolbarRef.current?.querySelector("button")?.focus();
      }
    };
    const dismissOutside = (event) => {
      if (!toolbarRef.current?.contains(event.target) && !editorRef.current?.contains(event.target)) {
        setPosition(null); setLinkOpen(false);
      }
    };
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", focusToolbar);
    document.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      document.removeEventListener("selectionchange", update);
      document.removeEventListener("keydown", focusToolbar);
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [editorRef, disabled]);

  const command = (name, value) => {
    const range = savedRange.current;
    if (!range || !editorRef.current?.contains(range.commonAncestorContainer)) return;
    editorRef.current.focus();
    const selection = window.getSelection();
    selection.removeAllRanges(); selection.addRange(range);
    document.execCommand(name, false, value);
    onChange();
    setLinkOpen(false); setPosition(null);
  };
  if (!position) return null;
  return createPortal(<div ref={toolbarRef} style={position}
    className="rounded-xl bg-neutral-800 text-white shadow-lg p-1 text-sm"
    onMouseDown={(event) => { if (event.target.tagName !== "INPUT" && event.target.tagName !== "SELECT") event.preventDefault(); }}
    onKeyDown={(event) => {
      event.stopPropagation();
      if (event.key === "Escape") { setPosition(null); setLinkOpen(false); editorRef.current?.focus(); }
    }}>
    <div role="toolbar" aria-label="Text formatting" className="flex items-center gap-1">
      <button type="button" aria-label="Add link" title="Add link" className="rounded p-2 hover:bg-white/20 focus-visible:ring-2" onClick={() => { setLinkOpen(!linkOpen); setUrl(""); setError(""); }}><FiLink /></button>
      <button type="button" aria-label="Bold" title="Bold" className="rounded px-2 py-1 font-bold hover:bg-white/20 focus-visible:ring-2" onClick={() => command("bold")}>B</button>
      <button type="button" aria-label="Italic" title="Italic" className="rounded px-2 py-1 italic hover:bg-white/20 focus-visible:ring-2" onClick={() => command("italic")}>I</button>
      <select aria-label="Text style" defaultValue="" className="bg-neutral-800 rounded p-1 focus-visible:ring-2" onChange={(event) => {
        const value = event.target.value;
        if (value === "ul" || value === "ol") command(value === "ul" ? "insertUnorderedList" : "insertOrderedList");
        else command("formatBlock", value);
      }}>
        <option value="" disabled>Text</option><option value="p">Normal text</option>
        <option value="h2">Heading</option><option value="h3">Subheading</option>
        <option value="ul">Bullet list</option><option value="ol">Numbered list</option>
      </select>
    </div>
    {linkOpen && <form className="p-2" onSubmit={(event) => {
      event.preventDefault(); event.stopPropagation();
      if (!/^(https?:\/\/|mailto:)/i.test(url.trim())) { setError("Use an https://, http:// or mailto: link."); return; }
      command("createLink", url.trim());
    }}>
      <input autoFocus type="text" aria-label="Link URL" placeholder="https://..." value={url} onChange={(event) => setUrl(event.target.value)} className="rounded p-1 text-gray-900 w-44" />
      <button type="submit" className="ml-2 rounded px-2 py-1 hover:bg-white/20">Apply</button>
      {error && <p role="alert" className="mt-1 text-xs">{error}</p>}
    </form>}
  </div>, document.body);
}
